import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { startMetricsJob, fetchMetricsJob } from "@/lib/reports";
import { isRestructuration } from "@/lib/checklist";

const eur = (v) => (v == null ? "n/c" : `${Math.round(Number(v)).toLocaleString("fr-FR")} €`);

export function useReportMetrics(dossierId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let timer, cancelled = false;
        setLoading(true); setData(null);
        const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
        const finish = (d) => { if (!cancelled) { setData(d); setLoading(false); } stop(); };
        (async () => {
            try {
                const res = await startMetricsJob(dossierId);
                if (res.stage === "done") { finish(res.result || null); return; }
                if (res.stage === "error" || !res.job_id) { finish(null); return; }
                timer = setInterval(async () => {
                    try {
                        const j = await fetchMetricsJob(dossierId, res.job_id);
                        if (j.stage === "done") finish(j.result || null);
                        else if (j.stage === "error") finish(null);
                    } catch { finish(null); }
                }, 3000);
            } catch { finish(null); }
        })();
        return () => { cancelled = true; stop(); };
    }, [dossierId]);
    return { data, loading };
}

function TopList({ label, top, testId }) {
    const line = (lst) => (lst?.length ? lst.map((x) => `${x.tiers} (${eur(x.montant)})${x.gelee ? " · GELÉE" : ""}`).join(" · ") : "—");
    return (
        <div data-testid={testId} className="text-xs">
            <span className="font-semibold text-foreground">{label}</span>
            <div className="mt-0.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <p><span className="uppercase tracking-wider text-amber-700">Intra-groupe :</span> <span className="text-muted-foreground">{line(top?.intra)}</span></p>
                <p><span className="uppercase tracking-wider text-muted-foreground">Externe :</span> <span className="text-muted-foreground">{line(top?.externe)}</span></p>
            </div>
        </div>
    );
}

export default function ReportKPIs({ data, loading }) {
    if (loading) return <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Calcul des métriques…</p>;
    if (!data) return null;
    const k = data.kpis || {};
    const isResto = isRestructuration(data.contexte_audit);
    const runway = k.cash_runway_mois;
    const items = isResto ? [
        { t: "dcp", label: "DCP estimée", v: k.dcp || "n/c", alert: k.dcp && k.dcp !== "n/c" },
        { t: "cash-burn", label: "Cash burn mensuel", v: eur(k.cash_burn_mensuel), alert: k.cash_burn_mensuel > 0 },
        { t: "dettes-fournisseurs", label: "Dettes fournisseurs (solde 40, D−C)", v: eur(k.dettes_fournisseurs) },
        { t: "creances-clients", label: "Créances clients (solde 41, D−C)", v: eur(k.creances_clients) },
    ] : [
        { t: "point-mort", label: "Point Mort (CA annuel)", v: k.point_mort_ca != null ? eur(k.point_mort_ca) : "n/c" },
        { t: "cash-runway", label: "Cash Runway (mois de survie)", v: runway === "rentable" ? "Rentable" : (runway != null ? `${runway} mois` : "n/c"), alert: typeof runway === "number" && runway < 6 },
        { t: "dettes-fournisseurs", label: "Dettes fournisseurs", v: eur(k.dettes_fournisseurs) },
        { t: "creances-clients", label: "Créances clients", v: eur(k.creances_clients) },
    ];
    const ig = data.intra_groupe;
    return (
        <div data-testid="report-kpis" className="rounded-md border border-border bg-muted/50 px-5 py-4 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {items.map((m) => (
                    <div key={m.t} data-testid={`kpi-${m.t}`} className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3">
                        <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{m.label}</p>
                        <p className={`mt-1 font-display text-xl tabular-nums ${m.alert ? "text-rose-700" : "text-foreground"}`}>{m.v}</p>
                    </div>
                ))}
            </div>
            {!k.dcp && k.dettes_fournisseurs == null && <p data-testid="kpi-hint" className="text-xs text-muted-foreground">Déposez et extrayez un FEC de l&apos;entité (Dataroom) pour calculer la DCP, le cash burn, les dettes fournisseurs et les créances clients.</p>}
            {ig && isResto && (
                <div className="space-y-2 border-t border-border pt-3">
                    <TopList label="Top 2 fournisseurs" top={ig.top_fournisseurs} testId="top-fournisseurs" />
                    <TopList label="Top 2 clients" top={ig.top_clients} testId="top-clients" />
                    {ig.alertes?.length > 0 && (
                        <div data-testid="intra-alertes" className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 space-y-1">
                            {ig.alertes.map((a) => <p key={`${a.entite}-${a.compte}-${a.montant}`} className="text-xs text-rose-800"><b>ALERTE ROUGE</b> — {a.entite} en procédure collective, {a.nature} de {eur(a.montant)} non gelée (compte {a.compte}).</p>)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
